import { SlidersHorizontal } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { customFieldId } from "@/lib/customFieldFilters";
import { FieldVisibilitySection } from "./FieldVisibilitySection";
import {
  companyCustomFieldId,
  OPPORTUNITY_CARD_FIELDS,
  quoteCustomFieldId,
} from "./opportunityCardFields";

interface OpportunityFieldVisibilityMenuProps {
  visibleFieldIds: string[];
  onToggle: (fieldId: string) => void;
  customFieldNames: string[];
  quoteCustomFieldNames: string[];
  companyCustomFieldNames: string[];
}

/**
 * Renders the board's "Field Visibility" control: a checklist of the built-in
 * card fields and the organization's opportunity, quote, and company custom
 * fields, deciding what each card shows.
 * @param visibleFieldIds - Ids of the fields the cards currently show
 * @param onToggle - Handler called with the id of the field to show or hide
 * @param customFieldNames - The organization's opportunity custom field names, in schema order
 * @param quoteCustomFieldNames - The organization's quote custom field names, in schema order
 * @param companyCustomFieldNames - The organization's company custom field names, in schema order
 */
export function OpportunityFieldVisibilityMenu({
  visibleFieldIds,
  onToggle,
  customFieldNames,
  quoteCustomFieldNames,
  companyCustomFieldNames,
}: OpportunityFieldVisibilityMenuProps) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline">
          <SlidersHorizontal className="h-4 w-4" />
          Field Visibility
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel>Field Visibility</DropdownMenuLabel>
        {OPPORTUNITY_CARD_FIELDS.map((field) => (
          <DropdownMenuCheckboxItem
            key={field.id}
            checked={visibleFieldIds.includes(field.id)}
            onCheckedChange={() => onToggle(field.id)}
            onSelect={(e) => e.preventDefault()}
          >
            {field.label}
          </DropdownMenuCheckboxItem>
        ))}
        <FieldVisibilitySection
          label="Opportunity custom fields"
          fieldNames={customFieldNames}
          idFor={customFieldId}
          visibleFieldIds={visibleFieldIds}
          onToggle={onToggle}
        />
        <FieldVisibilitySection
          label="Quote custom fields"
          fieldNames={quoteCustomFieldNames}
          idFor={quoteCustomFieldId}
          visibleFieldIds={visibleFieldIds}
          onToggle={onToggle}
        />
        <FieldVisibilitySection
          label="Company custom fields"
          fieldNames={companyCustomFieldNames}
          idFor={companyCustomFieldId}
          visibleFieldIds={visibleFieldIds}
          onToggle={onToggle}
        />
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
